import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Alert, Button, Carousel, List, Spin, PaperIcon } from '@nackle/paper';
import { useLazyQuery } from '@apollo/client';
import { getString } from '@nackle/intl-tools';
import { COURSE_LIST_QUERY } from '../../gql';
import { CourseListItem, CourseCarouseItem } from '../../components';
import PropTypes from 'prop-types';
import './styles.less';

const CourseList = ({
  accessToken,
  courseThumbnailPlaceholder,
  lePlayerDomain,
  pageSize,
  locale,
  imageSize,
  imageShape,
  layout,
  paginatorPosition,
  paginatorSize,
  buttonSize,
  buttonShape,
  showDescription,
  listSize,
  showCompleteIcon,
  showPlayIcon,
  showResumeIcon,
  paginateRequest,
  orderBy,
  orderAscending,
  excludeCompletedCourses,
  useSegmentationFilter,
  maxImageWidth,
  maxImageHeight
}) => {
  var _queryData$courses, _queryData$courses$pa;

  const [position, setPosition] = useState(1);
  const [disableButtons, setDisableButtons] = useState(false);
  const carouselRef = useRef();

  const handleCarouselNext = () => {
    setDisableButtons(true);

    if (position + 1 > courseCount) {
      setPosition(1);
    } else {
      setPosition(position + 1);
    }

    carouselRef.current.next();
  };

  const handleCarouselPrev = () => {
    setDisableButtons(true);

    if (position - 1 === 0) {
      setPosition(courseCount);
    } else {
      setPosition(position - 1);
    }

    carouselRef.current.prev();
  };

  const handleAfterChange = () => {
    setDisableButtons(false);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [getCoursePage, {
    loading: queryLoading,
    error: queryError,
    data: queryData
  }] = useLazyQuery(COURSE_LIST_QUERY, {
    onCompleted: data => {
      console.log(data);
    }
  });
  useEffect(() => {
    const reqVars = paginateRequest ? {
      limit: pageSize,
      page: currentPage,
      orderBy,
      orderDir: orderAscending ? 'asc' : 'desc',
      withCompleted: !excludeCompletedCourses,
      useSegmentationFilter,
      maxWidth: maxImageWidth,
      maxHeight: maxImageHeight
    } : {
      //limit: 3, 
      page: 1,
      orderBy,
      orderDir: orderAscending ? 'asc' : 'desc',
      withCompleted: !excludeCompletedCourses,
      useSegmentationFilter,
      maxWidth: maxImageWidth,
      maxHeight: maxImageHeight
    };
    getCoursePage({
      variables: reqVars
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, orderBy, orderAscending, excludeCompletedCourses, useSegmentationFilter, paginateRequest, currentPage]);
  const isLoading = useMemo(() => {
    return queryLoading && !queryError;
  }, [queryLoading, queryError]);
  const hasError = useMemo(() => {
    if (queryError && !queryLoading) {
      console.error(queryError);
      return true;
    } else {
      return false;
    }
  }, [queryError, queryLoading]);
  const courses = useMemo(() => {
    if (queryData && !queryError) {
      return queryData.courses.edges.map(course => {
        var _course$node, _course$node3, _foundLocale, _foundLocale2, _course$enrollment;

        let foundLocale = (_course$node = course.node) === null || _course$node === void 0 ? void 0 : _course$node.locales.find(c => c.locale === locale); // if locale not found look for en-US, if that is not found use the first one

        if (!foundLocale) {
          var _course$node2;

          foundLocale = ((_course$node2 = course.node) === null || _course$node2 === void 0 ? void 0 : _course$node2.locales.find(c => c.locale === 'en-US')) || course.locales[0];
        }

        const courseId = (_course$node3 = course.node) === null || _course$node3 === void 0 ? void 0 : _course$node3.id;
        const path = `${lePlayerDomain}/course/${courseId}` + `?token=${accessToken}` + `&locale=${locale}` + `&referrer=${encodeURIComponent(location.href)}`;
        return {
          id: courseId,
          title: ((_foundLocale = foundLocale) === null || _foundLocale === void 0 ? void 0 : _foundLocale.name) || '',
          description: ((_foundLocale2 = foundLocale) === null || _foundLocale2 === void 0 ? void 0 : _foundLocale2.description) || '',
          thumbnail: course.node.thumbnailImage || {
            url: courseThumbnailPlaceholder
          },
          status: (_course$enrollment = course.enrollment) === null || _course$enrollment === void 0 ? void 0 : _course$enrollment.status,
          path,
          key: course.node.id
        };
      });
    }
  }, [queryData, queryError, locale, courseThumbnailPlaceholder, lePlayerDomain, accessToken]);
  const courseCount = useMemo(() => {
    return (courses === null || courses === void 0 ? void 0 : courses.length) || 0;
  }, [courses]);
  const hasCourses = useMemo(() => {
    return courses && queryData && !queryLoading && !queryError;
  }, [courses, queryData, queryLoading, queryError]);
  return /*#__PURE__*/React.createElement("div", {
    className: "course-list"
  },
  /* Loading Spinner */
  isLoading && /*#__PURE__*/React.createElement("div", {
    className: "loading-spinner",
    style: {
      textAlign: 'center',
      paddingTop: '2rem',
      paddingBottom: '2rem'
    }
  }, /*#__PURE__*/React.createElement(Spin, {
    size: "small"
  })),
  /* Error message  */
  hasError && /*#__PURE__*/React.createElement(Alert, {
    message: getString('error.title'),
    description: getString('error.description'),
    type: "error",
    closable: true,
    showIcon: true
  }),
  /* Course List  */
  hasCourses && layout !== 'carousel' && /*#__PURE__*/React.createElement(List, {
    listSize: listSize,
    dataSource: courses,
    itemLayout: layout,
    locale: {
      emptyText: getString('courses.none')
    },
    pagination: paginateRequest ? {
      paginatorSize,
      current: currentPage,
      total: Math.max(courses === null || courses === void 0 ? void 0 : courses.length, queryData === null || queryData === void 0 ? void 0 : (_queryData$courses = queryData.courses) === null || _queryData$courses === void 0 ? void 0 : (_queryData$courses$pa = _queryData$courses.pageInfo) === null || _queryData$courses$pa === void 0 ? void 0 : _queryData$courses$pa.totalCount, 0),
      pageSize,
      hideOnSinglePage: true,
      showSizeChanger: false,
      position: paginatorPosition,
      onChange: page => setCurrentPage(page)
    } : null,
    renderItem: item => /*#__PURE__*/React.createElement(CourseListItem, {
      item: item,
      size: imageSize,
      shape: imageShape,
      buttonSize: buttonSize,
      buttonShape: buttonShape,
      showDescription: showDescription,
      showCompleteIcon: showCompleteIcon,
      showPlayIcon: showPlayIcon,
      showResumeIcon: showResumeIcon
    })
  }),
  /* Carousel   */
  hasCourses && layout === 'carousel' && /*#__PURE__*/React.createElement("div", {
    className: "carousel-list"
  }, /*#__PURE__*/React.createElement(Carousel, {
    ref: carouselRef,
    dots: false,
    afterChange: handleAfterChange
  }, courses.map(item => /*#__PURE__*/React.createElement(CourseCarouseItem, {
    key: item.key,
    item: item,
    size: imageSize,
    shape: imageShape,
    buttonSize: buttonSize,
    buttonShape: buttonShape,
    showDescription: showDescription,
    showCompleteIcon: showCompleteIcon,
    showPlayIcon: showPlayIcon,
    showResumeIcon: showResumeIcon
  }))), /*#__PURE__*/React.createElement("div", {
    className: "buttons"
  }, /*#__PURE__*/React.createElement(Button, {
    className: "button-prev",
    value: "small",
    disabled: disableButtons,
    onClick: handleCarouselPrev
  }, /*#__PURE__*/React.createElement(PaperIcon, {
    icon: "ArrowLeft",
    collection: "lindua"
  })), /*#__PURE__*/React.createElement("span", null, `${position}/${courseCount}`), /*#__PURE__*/React.createElement(Button, {
    className: "button-next",
    value: "small",
    disabled: disableButtons,
    onClick: handleCarouselNext
  }, /*#__PURE__*/React.createElement(PaperIcon, {
    icon: "ArrowRight",
    collection: "lindua"
  })))));
};

CourseList.propTypes = {
  accessToken: PropTypes.any,
  courseThumbnailPlaceholder: PropTypes.any,
  lePlayerDomain: PropTypes.any,
  pageSize: PropTypes.number,
  locale: PropTypes.string,
  imageSize: PropTypes.string,
  imageShape: PropTypes.string,
  layout: PropTypes.string,
  paginatorPosition: PropTypes.string,
  paginatorSize: PropTypes.string,
  buttonSize: PropTypes.string,
  buttonShape: PropTypes.string,
  showDescription: PropTypes.bool,
  listSize: PropTypes.string,
  showCompleteIcon: PropTypes.bool,
  showPlayIcon: PropTypes.bool,
  showResumeIcon: PropTypes.bool,
  paginateRequest: PropTypes.bool,
  orderBy: PropTypes.oneOf(['id', 'name', 'createdAt', 'ratingAverage']),
  // not used without paginateRequest = true
  orderAscending: PropTypes.bool,
  // not used without paginateRequest = true
  excludeCompletedCourses: PropTypes.bool,
  // not used without paginateRequest = true
  useSegmentationFilter: PropTypes.bool,
  maxImageWidth: PropTypes.number,
  maxImageHeight: PropTypes.number
};
CourseList.defaultProps = {
  pageSize: 5,
  locale: 'en-US',
  imageSize: 'large',
  imageShape: 'square',
  layout: 'horizontal',
  paginatorPosition: 'bottom',
  paginatorSize: 'small',
  buttonSize: 'small',
  buttonShape: 'rectangle',
  showDescription: false,
  listSize: 'small',
  showCompleteIcon: true,
  showPlayIcon: false,
  showResumeIcon: false,
  paginateRequest: false,
  orderBy: 'id',
  orderAscending: true,
  excludeCompletedCourses: false,
  useSegmentationFilter: true,
  maxImageWidth: 200,
  maxImageHeight: 200
};
export default CourseList;
//# sourceMappingURL=CourseList.js.map